import { Component, Input, Output, EventEmitter, ElementRef, HostListener, ViewChild, effect, inject, ChangeDetectionStrategy, DestroyRef, signal } from '@angular/core';
import { AppSettings } from '@services/settings/app-settings.service';
import { CommonModule } from '@angular/common';
import { UserService } from '@services/core/user.service';
import { ModuleService } from '@services/core/module.service';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { buildRouteMap, resolveMenus } from './sidebar-route.utils';
import { findSidebar, findProfile, syncProfileMenu, toggleProfile, expandTimeOf, isMobileWidth, bindMenuClick, hasStorage, savedScroll, nextActive, nextInactive } from './sidebar-dom.utils';
import { floatLeft, calcFloatPos, FloatPos } from './sidebar-position.utils';

@Component({
	selector: 'sidebar',
	templateUrl: './sidebar.component.html',
	changeDetection: ChangeDetectionStrategy.Default,
	imports: [CommonModule, RouterLink, RouterLinkActive, NgbTooltipModule]
})

export class SidebarComponent {
	navProfileState = "collapse";
	menus: any[] = [];
	@ViewChild('sidebarScrollbar', { static: false }) private sidebarScrollbar: ElementRef;
	@Output() appSidebarMinifiedToggled = new EventEmitter<boolean>();
	@Output() hideMobileSidebar = new EventEmitter<boolean>();
	@Output() setPageFloatSubMenu = new EventEmitter();

	@Output() appSidebarMobileToggled = new EventEmitter<boolean>();
	@Input() appSidebarTransparent;
	@Input() appSidebarGrid;
	@Input() appSidebarFixed;
	@Input() appSidebarMinified;

	public appSettings: AppSettings = inject(AppSettings)

	appSidebarFloatSubMenu;
	appSidebarFloatSubMenuHide;
	appSidebarFloatSubMenuHideTime = 250;
	appSidebarFloatSubMenuTop;
	appSidebarFloatSubMenuLeft = '60px';
	appSidebarFloatSubMenuRight;
	appSidebarFloatSubMenuBottom;
	appSidebarFloatSubMenuArrowTop;
	appSidebarFloatSubMenuArrowBottom;
	appSidebarFloatSubMenuLineTop;
	appSidebarFloatSubMenuLineBottom;
	appSidebarFloatSubMenuOffset;

	mobileMode;
	desktopMode;
	scrollTop;
	entity
	userService = inject(UserService)
	moduleService = inject(ModuleService)
	destroyRef = inject(DestroyRef)
	private routeMap = signal<Map<string, string>>(new Map())

	toggleNavProfile(e) {
		e.preventDefault();
		this.applyProfile(e.target.closest('.menu-profile'));
	}

	private applyProfile(menu: Element | null) {
		const sidebar = findSidebar();
		const profile = findProfile();
		const time = expandTimeOf(sidebar, 250);
		if (profile && profile.style) this.syncMenu(menu, profile, time);
	}

	private syncMenu(menu: Element | null, profile: HTMLElement, time: number) {
		syncProfileMenu(menu, profile);
		toggleProfile(profile, time);
	}

	toggleAppSidebarMinified() {
		this.appSidebarMinifiedToggled.emit(true);
		this.navProfileState = "collapse";
		this.scrollTop = 40;
	}

	calculateAppSidebarFloatSubMenuPosition() {
		const top = this.appSidebarFloatSubMenuOffset.top;
		const height = window.innerHeight;
		setTimeout(() => this.applyFloat(top, height));
	}

	private applyFloat(top: number, height: number) {
		const elm = document.querySelector('.app-sidebar-float-submenu-container') as HTMLElement;
		const bar = document.getElementById('sidebar') as HTMLElement;
		this.appSidebarFloatSubMenuRight = 'auto';
		this.appSidebarFloatSubMenuLeft = floatLeft(this.appSidebarFloatSubMenuOffset.width, bar.offsetLeft);
		this.applyPos(calcFloatPos(height, top, elm.offsetHeight, top));
	}

	private applyPos(pos: FloatPos) {
		this.appSidebarFloatSubMenuTop = pos.top;
		this.appSidebarFloatSubMenuBottom = pos.bottom;
		this.appSidebarFloatSubMenuArrowTop = pos.arrowTop;
		this.applyLines(pos);
	}

	private applyLines(pos: FloatPos) {
		this.appSidebarFloatSubMenuArrowBottom = pos.arrowBottom;
		this.appSidebarFloatSubMenuLineTop = pos.lineTop;
		this.appSidebarFloatSubMenuLineBottom = pos.lineBottom;
	}

	showAppSidebarFloatSubMenu(menu, e) {
		if (!this.appSettings.appSidebarMinified) return;
		this.keepFloat(menu, e);
	}

	private keepFloat(menu: any, e: any) {
		clearTimeout(this.appSidebarFloatSubMenuHide);
		this.appSidebarFloatSubMenu = menu;
		this.appSidebarFloatSubMenuOffset = e.target.getBoundingClientRect();
		this.calculateAppSidebarFloatSubMenuPosition();
	}

	hideAppSidebarFloatSubMenu() {
		this.appSidebarFloatSubMenuHide = setTimeout(() => this.clearFloat(), this.appSidebarFloatSubMenuHideTime);
	}

	private clearFloat() {
		this.appSidebarFloatSubMenu = '';
	}

	remainAppSidebarFloatSubMenu() {
		clearTimeout(this.appSidebarFloatSubMenuHide);
	}

	@HostListener('scroll', ['$event'])
	onScroll(event) {
		this.scrollTop = this.scrollFor(event.srcElement.scrollTop);
		if (hasStorage()) localStorage.setItem('sidebarScroll', event.srcElement.scrollTop);
	}

	private scrollFor(top: number): number {
		if (this.appSettings.appSidebarMinified) return top + 40;
		return 0;
	}

	@HostListener('window:resize', ['$event'])
	onResize(event) {
		this.applyMode(window.innerWidth);
	}

	private applyMode(width: number) {
		if (isMobileWidth(width)) return this.setMobile();
		this.setDesktop();
	}

	private setMobile() {
		this.mobileMode = true;
		this.desktopMode = false;
	}

	private setDesktop() {
		this.mobileMode = false;
		this.desktopMode = true;
	}

	ngAfterViewChecked() {
		if (hasStorage() && savedScroll()) this.restoreScroll();
	}

	private restoreScroll() {
		if (this.sidebarScrollbar && this.sidebarScrollbar.nativeElement) this.applyScroll();
	}

	private applyScroll() {
		this.sidebarScrollbar.nativeElement.scrollTop = savedScroll();
	}

	handleSidebarMenuToggle(menus, expandTime) {
		for (const menu of menus) bindMenuClick(menu, menus, expandTime)
	}

	handleSidebarMenuToggled() {
		const sidebar = findSidebar();
		const time = expandTimeOf(sidebar, 300);
		this.bindLevels(time);
	}

	private bindLevels(time: number) {
		this.bindLevel('.app-sidebar .menu > .menu-item.has-sub > .menu-link', time);
		this.bindLevel('.app-sidebar .menu > .menu-item.has-sub > .menu-submenu > .menu-item.has-sub > .menu-link', time);
		this.bindLevel('.app-sidebar .menu > .menu-item.has-sub > .menu-submenu > .menu-item.has-sub > .menu-submenu > .menu-item.has-sub > .menu-link', time);
	}

	private bindLevel(selector: string, time: number) {
		const nodes = [].slice.call(document.querySelectorAll(selector));
		this.bindNodes(nodes, time);
	}

	private bindNodes(nodes: any[], time: number) {
		for (const node of nodes) bindMenuClick(node, nodes, time)
	}

	expandCollapseSubmenu(currentMenu, allMenu, active) {
		this.collapseRest(currentMenu, allMenu);
		this.toggleCurrent(currentMenu, active);
	}

	private collapseRest(current: any, all: any[]) {
		for (const menu of all) this.collapseOneState(menu, current)
	}

	private collapseOneState(menu: any, current: any) {
		if (menu != current) menu.state = 'collapse';
	}

	private toggleCurrent(current: any, active: any) {
		if (active.isActive) return this.toggleActive(current);
		this.toggleInactive(current);
	}

	private toggleActive(current: any) {
		current.state = nextActive(current.state);
	}

	private toggleInactive(current: any) {
		current.state = nextInactive(current.state);
	}

	constructor(

	) {
		this.applyMode(window.innerWidth);

		this.moduleService.getActiveModules().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => this.saveRoutes(res))

		effect(() => {
			this.menus = resolveMenus(this.userService.sharingMenu(), this.routeMap())
			setTimeout(() => {
				this.handleSidebarMenuToggled()
			})
		})

		effect(() => {
			this.entity = this.userService.sharingEntity()
		})

	}

	private saveRoutes(res: any[]) {
		this.routeMap.set(buildRouteMap(res))
	}
}
